import { useEffect, useMemo } from 'react';
import { getSortField, getSortValue, sortKey } from '../../../../../features/table/helpers';
import { formatDateTimeRangeFromString } from '../../../../../helpers/date-time';
import {
  defaultPage,
  defaultPageSize,
  defaultViewStyle,
  pageKey,
  pageSizeKey,
  viewStyleKey,
  withArchiveKey,
} from '../../../../../helpers/path';
import { useLang } from '../../../../../hooks/use-lang';
import { useModalParams } from '../../../../../hooks/use-modal-params';
import { useQueryParams } from '../../../../../hooks/use-query-params';
import { FetchQrListRequest } from '../../../../../store/api/qr-api';
import { useBranchesStore } from '../../../../../store/use-branch-store';
import { useQrStore } from '../../../../../store/use-qr-store';
import { useTagStaffStore } from '../../../../../store/use-tag-staff-store';
import { SortType } from '../../../../../types/props';

export const useQrList = () => {
  const { fetchQrList, deleteQrCode, restoreQrCode, pagination, isLoadingFetch } = useQrStore();
  const { fetchBranches } = useBranchesStore();
  const { fetchTags } = useTagStaffStore();

  const { t } = useLang();

  const { getQueryParams } = useQueryParams();
  const params = getQueryParams();

  const name = params.name;
  const description = params.description;
  const id = params.id;
  const tags = params.tags;
  const services = params.services;
  const branchId = params['branch'];
  const createdAt = params['created_at'];
  const ts = params['ts'];
  const page = params[pageKey] || defaultPage;
  const pageSize = params[pageSizeKey] || defaultPageSize;
  const viewStyle = params[viewStyleKey] || defaultViewStyle;
  const sort = params[sortKey];
  const sortField = getSortField(sort);
  const sortDirection = getSortValue(sort) as SortType;
  const with_archive = !!params[withArchiveKey];

  useEffect(() => {
    fetchBranches({ since_id: 0, n: 1000, with_archive: true });
    fetchTags({ is_by_company: true, n: 1000, since_id: 0 });
  }, []);

  const requestParams: FetchQrListRequest = useMemo(() => {
    const parsedCreatedAt = formatDateTimeRangeFromString(createdAt);
    return {
      name,
      description,
      id,
      tags,
      services,
      page,
      page_size: pageSize,
      from: parsedCreatedAt?.from,
      to: parsedCreatedAt?.to,
      is_by_company: true,
      places: branchId,
      field: sortField,
      dir: sortDirection,
      with_archive,
    };
  }, [
    branchId,
    with_archive,
    ts,
    name,
    description,
    id,
    tags,
    services,
    sortField,
    sortDirection,
    createdAt,
    page,
    pageSize,
    viewStyle,
  ]);

  useEffect(() => {
    fetchQrList(requestParams);
  }, [requestParams]);

  const { handleCloseModal: handleCloseQrModal, isOpen: isOpenQrModal } =
    useModalParams('viewStaticQr');

  const handleEnd = () => {
    if (isLoadingFetch || !pagination || pagination.is_last) return;
    const nextPage = pagination.page + 1;
    fetchQrList({ ...requestParams, page: nextPage, infinite: true });
  };

  return {
    handleCloseQrModal,
    isOpenQrModal,
    t,
    deleteQrCode,
    restoreQrCode,
    viewStyle,
    pagination,
    handleEnd,
  };
};
